import { useCallback, useEffect, useState } from 'react';
import { v2, type StatusSnapshot } from '@/api/v2Client';
import { subscribeSubmissionRefresh } from '@/lib/submissionRefresh';

export interface StatusEvidence {
  readonly snapshot: StatusSnapshot | null;
  readonly loading: boolean;
  readonly error: boolean;
}
export const LOADING_STATUS: StatusEvidence = { snapshot: null, loading: true, error: false };

/** Page-scoped evidence: deduplicated, bounded reads, with no cross-user cache. */
export function useStatusEvidence(
  submissionIds: readonly string[], refreshToken?: unknown, documentToken?: unknown, metadataToken?: unknown,
): { get: (id: string) => StatusEvidence; refresh: () => void } {
  const idsKey = JSON.stringify([...new Set(submissionIds.filter(Boolean))].sort());
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const [result, setResult] = useState<{
    key: string; revision: number; refreshToken: unknown; documentToken: unknown; metadataToken: unknown;
    entries: Readonly<Record<string, StatusEvidence>>;
  } | null>(null);
  useEffect(() => subscribeSubmissionRefresh(refresh), [refresh]);
  useEffect(() => {
    let cancelled = false;
    const ids = JSON.parse(idsKey) as string[];
    const entries: Record<string, StatusEvidence> = {};
    let index = 0;
    const worker = async (): Promise<void> => {
      while (!cancelled && index < ids.length) {
        const id = ids[index++]!;
        let entry: StatusEvidence;
        try {
          const snapshot = await v2.submissions.getStatus(id);
          if (snapshot.submissionId.toLowerCase() !== id.toLowerCase()) throw new Error('Mismatched submission');
          entry = { snapshot, loading: false, error: false };
        } catch {
          entry = { snapshot: null, loading: false, error: true };
        }
        if (cancelled) return;
        entries[id] = entry;
        setResult({ key: idsKey, revision, refreshToken, documentToken, metadataToken, entries: { ...entries } });
      }
    };
    void Promise.all(Array.from({ length: Math.min(4, ids.length) }, worker));
    return () => { cancelled = true; };
  }, [idsKey, revision, refreshToken, documentToken, metadataToken]);
  const current = result?.key === idsKey && result.revision === revision && result.refreshToken === refreshToken
    && result.documentToken === documentToken && result.metadataToken === metadataToken;
  return { get: (id) => current ? result.entries[id] ?? LOADING_STATUS : LOADING_STATUS, refresh };
}
